/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jul 23 2024
 **/

import { atom } from "jotai";
import { BaseService } from "@/frameworks/libraries/restservice";

class GroupCall extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (callId?: string, path?: string) =>
		`/api/group_calls${callId ? `/${callId}` : ""}${path ? `/${path}` : ""}`;

	private static Cache = {
		Delete: atom<WebServices.GroupCall.Delete.Response>({}),
		Detail: atom<WebServices.GroupCall.Detail.Response>({}),
		Recent: atom<WebServices.GroupCall.Recent.Response>({}),
		Between: atom<WebServices.GroupCall.Between.Response>({}),
		Create: atom<WebServices.GroupCall.Create.Response>({}),
		Add: atom<WebServices.GroupCall.Add.Response>({}),
		DeleteAll: atom<WebServices.GroupCall.DeleteAll.Response>({}),
		Update: atom<WebServices.GroupCall.Update.Response>({})
	};

	public remove = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ callId, ...parameter }: WebServices.GroupCall.Delete.Request) =>
			this.delete(this.APIPath(callId), parameter),
		cache: GroupCall.Cache.Delete
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ callId, ...parameter }: WebServices.GroupCall.Detail.Request) =>
			this.get(this.APIPath(callId), parameter),
		cache: GroupCall.Cache.Detail
	});

	public recent = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.GroupCall.Recent.Request) => this.get(this.APIPath(), parameter),
		cache: GroupCall.Cache.Recent
	});

	public between = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.GroupCall.Between.Request) => this.get(this.APIPath("between"), parameter),
		cache: GroupCall.Cache.Between
	});

	public create = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.GroupCall.Create.Request) => this.post(this.APIPath(), parameter),
		cache: GroupCall.Cache.Create
	});

	public add = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ callId, ...parameter }: WebServices.GroupCall.Add.Request) =>
			this.post(this.APIPath(callId, "users"), parameter),
		cache: GroupCall.Cache.Add
	});

	public update = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ callId, ...parameter }: WebServices.GroupCall.Update.Request) =>
			this.put(this.APIPath(callId), parameter),
		cache: GroupCall.Cache.Update
	});

	public removeAll = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.GroupCall.DeleteAll.Request) =>
			this.post(this.APIPath("delete_all"), parameter),
		cache: GroupCall.Cache.DeleteAll
	});
}

class GroupCallServiceStatic {
	constructor(public readonly groupCall = new GroupCall()) {}
}

const GroupCallService = new GroupCallServiceStatic();
export default GroupCallService;
